/**
 * 红色模版独立导航栏
 * 
 * 设计理念：简洁紧凑，信息型网站风格
 * - 始终白色背景，不用透明/滚动变化
 * - 导航居中，功能按钮右侧
 * - 红底发布按钮为唯一 CTA
 */

import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useSite } from "@/context/SiteContext";
import { useSiteConfig } from "@/context/SiteConfigContext";

export default function RedNavbar() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [logo, setLogo] = useState("");
  const [logoError, setLogoError] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isLoggedIn, isAdmin, logout } = useAuth();
  const { siteId, loading: siteLoading, siteName: contextSiteName } = useSite();
  const { siteConfig, navMenus, navCategories } = useSiteConfig();
  const enableVip = siteConfig?.enable_vip ?? true;

  const siteName = contextSiteName;

  useEffect(() => {
    if (!siteConfig) return;
    if (siteConfig.logo) {
      setLogo(siteConfig.logo);
      setLogoError(false);
    }
  }, [siteConfig]);

  const handleLogout = () => {
    logout();
    setIsUserMenuOpen(false);
    navigate("/");
  };

  const isHome = location.pathname === "/";

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-100 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-14 md:h-15">

          {/* ── 左侧：Logo + 站点名 ── */}
          <Link to="/" className="flex items-center gap-2.5 shrink-0">
            {logo && !logoError ? (
              <img
                src={logo}
                alt={siteName}
                className="h-9 md:h-10 w-auto object-contain"
                onError={() => setLogoError(true)}
              />
            ) : (
              <div className="h-9 w-9 rounded-md bg-red-600 flex items-center justify-center text-white font-bold text-sm">
                {siteName ? siteName.charAt(0).toUpperCase() : 'S'}
              </div>
            )}
            {siteName && (
              <span className="text-base md:text-lg font-bold text-gray-800 tracking-tight">
                {siteName}
              </span>
            )}
          </Link>

          {/* ── 中间：主导航（桌面端） ── */}
          <div className="hidden lg:flex items-center gap-0.5">
            <Link
              to="/"
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                isHome
                  ? "text-red-600 bg-red-50"
                  : "text-gray-600 hover:text-red-600 hover:bg-red-50/50"
              }`}
            >
              首页
            </Link>
            {navMenus.map((menu) => (
              <Link
                key={menu.id}
                to={menu.type === 'category' ? `/category/${menu.target_url || ''}` :
                     menu.type === 'page' ? `/page/${menu.target_url || ''}` :
                     menu.type === 'link' ? (menu.target_url || '#') : '#'}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors text-gray-600 hover:text-red-600 hover:bg-red-50/50`}
              >
                {menu.name}
              </Link>
            ))}
            {navCategories.map((cat) => (
              <Link
                key={cat.id}
                to={`/category/${cat.slug}`}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  location.pathname === `/category/${cat.slug}`
                    ? "text-red-600 bg-red-50"
                    : "text-gray-600 hover:text-red-600 hover:bg-red-50/50"
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>

          {/* ── 右侧：用户区 + 发布按钮 ── */}
          <div className="flex items-center gap-2">
            {isLoggedIn ? (
              <div className="relative">
                <button
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <div className="w-7 h-7 rounded-full bg-red-100 flex items-center justify-center text-red-600 text-xs font-bold overflow-hidden">
                    {user?.avatar ? (
                      <img src={user.avatar} alt="" className="w-full h-full object-cover" />
                    ) : (
                      (user?.nickname || 'U').charAt(0).toUpperCase()
                    )}
                  </div>
                  <span className="text-sm text-gray-600 hidden md:inline max-w-[80px] truncate">
                    {user?.nickname}
                  </span>
                  <i className="ri-arrow-down-s-line text-gray-400 text-xs" />
                </button>

                {isUserMenuOpen && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={() => setIsUserMenuOpen(false)} />
                    <div className="absolute right-0 top-full mt-1.5 w-44 bg-white rounded-lg shadow-lg border border-gray-100 py-1.5 z-20">
                      <Link
                        to="/dashboard"
                        className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-600 hover:bg-red-50 hover:text-red-600 transition-colors"
                        onClick={() => setIsUserMenuOpen(false)}
                      >
                        <i className="ri-user-line" />会员中心
                      </Link>
                      <Link
                        to="/dashboard/my-listings"
                        className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-600 hover:bg-red-50 hover:text-red-600 transition-colors"
                        onClick={() => setIsUserMenuOpen(false)}
                      >
                        <i className="ri-file-list-line" />我的发布
                      </Link>
                      {enableVip && (
                        <Link
                          to="/vip"
                          className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-red-600 font-medium hover:bg-red-50 transition-colors"
                          onClick={() => setIsUserMenuOpen(false)}
                        >
                          <i className="ri-vip-crown-line" />开通VIP
                        </Link>
                      )}
                      {isAdmin && (
                        <>
                          <div className="border-t border-gray-100 my-1" />
                          <Link
                            to="/admin"
                            className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-600 hover:bg-red-50 hover:text-red-600 transition-colors"
                            onClick={() => setIsUserMenuOpen(false)}
                          >
                            <i className="ri-shield-star-line" />后台管理
                          </Link>
                        </>
                      )}
                      <div className="border-t border-gray-100 my-1" />
                      <button
                        onClick={handleLogout}
                        className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-500 hover:bg-gray-50 hover:text-red-500 transition-colors w-full text-left"
                      >
                        <i className="ri-logout-box-line" />退出登录
                      </button>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <button
                onClick={() => navigate("/login")}
                className="hidden sm:inline-flex items-center gap-1 px-3.5 py-2 text-sm font-medium text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <i className="ri-user-line" />
                <span>登录</span>
              </button>
            )}

            <button
              onClick={() => navigate("/publish")}
              className="inline-flex items-center gap-1 px-3.5 py-2 text-sm font-bold bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors shadow-sm"
            >
              <i className="ri-add-line text-base" />
              <span className="hidden sm:inline">立即发布</span>
            </button>

            {/* 移动端菜单按钮 */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <i className={`ri-${isMobileMenuOpen ? "close" : "menu"}-line text-xl text-gray-600`} />
            </button>
          </div>
        </div>
      </div>

      {/* ── 移动端菜单 ── */}
      {isMobileMenuOpen && (
        <div className="lg:hidden border-t border-gray-100 bg-white">
          <div className="px-4 py-2 space-y-0.5 max-h-[60vh] overflow-y-auto">
            <Link
              to="/"
              onClick={() => setIsMobileMenuOpen(false)}
              className={`block px-3 py-2.5 text-sm font-medium rounded-lg transition-colors ${
                isHome ? "text-red-600 bg-red-50" : "text-gray-700 hover:bg-gray-50"
              }`}
            >
              <i className="ri-home-4-line mr-2" />首页
            </Link>
            {navMenus.map((menu) => (
              <Link
                key={menu.id}
                to={menu.type === 'category' ? `/category/${menu.target_url || ''}` :
                     menu.type === 'page' ? `/page/${menu.target_url || ''}` :
                     menu.type === 'link' ? (menu.target_url || '#') : '#'}
                onClick={() => setIsMobileMenuOpen(false)}
                className="block px-3 py-2.5 text-sm font-medium text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <i className="ri-menu-line mr-2" />{menu.name}
              </Link>
            ))}
            {navCategories.map((cat) => (
              <Link
                key={cat.id}
                to={`/category/${cat.slug}`}
                onClick={() => setIsMobileMenuOpen(false)}
                className={`block px-3 py-2.5 text-sm font-medium rounded-lg transition-colors ${
                  location.pathname === `/category/${cat.slug}` ? "text-red-600 bg-red-50" : "text-gray-700 hover:bg-gray-50"
                }`}
              >
                <i className="ri-grid-line mr-2" />{cat.name}
              </Link>
            ))}

            {/* VIP */}
            {enableVip && (
              <Link
                to="/vip"
                onClick={() => setIsMobileMenuOpen(false)}
                className="block px-3 py-2.5 text-sm font-medium text-red-600 rounded-lg hover:bg-red-50 transition-colors"
              >
                <i className="ri-vip-crown-line mr-2" />开通VIP
              </Link>
            )}

            <div className="border-t border-gray-100 my-1" />

            {isLoggedIn ? (
              <>
                <Link
                  to="/dashboard"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block px-3 py-2.5 text-sm font-medium text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <i className="ri-user-line mr-2" />会员中心
                </Link>
                <Link
                  to="/dashboard/my-listings"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block px-3 py-2.5 text-sm font-medium text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <i className="ri-file-list-line mr-2" />我的发布
                </Link>
                {isAdmin && (
                  <Link
                    to="/admin"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="block px-3 py-2.5 text-sm font-medium text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <i className="ri-shield-star-line mr-2" />后台管理
                  </Link>
                )}
                <button
                  onClick={() => {
                    handleLogout();
                    setIsMobileMenuOpen(false);
                  }}
                  className="block w-full text-left px-3 py-2.5 text-sm font-medium text-gray-500 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <i className="ri-logout-box-line mr-2" />退出登录
                </button>
              </>
            ) : (
              <Link
                to="/login"
                onClick={() => setIsMobileMenuOpen(false)}
                className="block px-3 py-2.5 text-sm font-medium text-red-600 rounded-lg hover:bg-red-50 transition-colors"
              >
                <i className="ri-user-line mr-2" />登录 / 注册
              </Link>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}
